import { BaseComponent } from '../../base-component';
import {
    DIMENSION_SELECTED_IN_SHEET_TOPIC,
    DIMENSION_SELECTED_TOPIC,
    LAST_SELECTED_VARIATION_ID_COOKIE_NAME,
    RELOAD_COMPONENT_TOPIC,
    RELOAD_DIMENSION_SELECTION_SHEET_COMPONENT_TOPIC,
} from '../../constants';
import { ColorUiType, DimensionValueByType, DimensionsStateEvent } from '../../dimension-selection/shared-types';
import { initializeOProduct } from '../../_common/client/product.const';
import { waitForCondition } from '../../_common/client/wait-for-condition';
import { generateEventMergeId, setGlobalEventMergeId, trackView } from '../../_common/client/tracking';
import { createStatusCodeErrorMessage, redirectToPreviousVariation } from './ui-aggregator-redirect';
import { VARIATION_LOAD_FAILED_EVENT } from '../../redirect-message/client/redirect-message.component';
import { cookie } from '@otto-ec/global-resources/cookie';

type SeoRedirectTrackingData = { product_SeoRedirect?: string; product_SeoRedirectOrigin?: string };

export class UiAggregatorComponent extends BaseComponent {
    public rootSelector = '.js_pdp_ui-aggregator';
    protected initialReferrer = '';
    private isLocked = false;
    private isRedirectToggleActive = false;
    private isErrorPageToggleActive = false;

    private async lockExecution(action: () => Promise<void>) {
        if (this.isLocked) {
            return;
        }

        this.isLocked = true;
        await action().finally(() => {
            this.isLocked = false;
        });
    }

    public async init(): Promise<void> {
        this.initialReferrer = document.referrer;
        this.isRedirectToggleActive = this.getRootElement().dataset.shouldRedirect === 'true';
        this.isErrorPageToggleActive = this.getRootElement().dataset.shouldShowErrorPage === 'true';

        initializeOProduct();

        await this.waitPageJsIsLoaded();

        await this.initVariationHash();

        await this.trackSeoRedirect();

        this.registerDimensionChangedEventHandler();

        this.setEnteredArticleNumberAndPromotionToOProduct();

        this.setTrackingEventMergeIdToOProduct(generateEventMergeId());

        this.writeVariationIdToCookie();

        this.emitComponentsHtmlLoadedEvent();

        this.registerDimensionChangedInDimensionSelectionSheetHandler();
    }

    public async onHtmlLoaded(): Promise<void> {
        return;
    }

    private async initVariationHash(): Promise<void> {
        const variationId = this.getVariationIdFromURL();
        const renderedVariationId = this.getRenderedVariationId();

        if (!variationId) {
            this.updateURL(renderedVariationId);
        } else if (renderedVariationId !== variationId) {
            await this.renderComponentsByVariationId(variationId);
        }
    }

    private async trackSeoRedirect() {
        const seoTrackingData = this.getAndRemoveSeoRedirectFromFragment();
        if (seoTrackingData.product_SeoRedirect || seoTrackingData.product_SeoRedirectOrigin) {
            trackView(seoTrackingData);
        }
    }

    private writeVariationIdToCookie() {
        const variationId = this.getVariationIdFromURL();
        if (variationId) {
            cookie.set(LAST_SELECTED_VARIATION_ID_COOKIE_NAME, variationId);
        }
    }

    private getAndRemoveSeoRedirectFromFragment(): SeoRedirectTrackingData {
        const fragment = o_util.fragment.deserialize<{ seoRedirect?: string; seoRedirectOrigin?: string }>(false);

        const seoTrackingData: SeoRedirectTrackingData = {
            product_SeoRedirect: fragment.seoRedirect,
            product_SeoRedirectOrigin: fragment.seoRedirectOrigin,
        };

        delete fragment.seoRedirectOrigin;
        delete fragment.seoRedirect;
        window.location.hash = o_util.fragment.serialize(fragment) ?? '';

        return seoTrackingData;
    }

    private getVariationIdFromURL(): string | undefined {
        const variationId = new URLSearchParams(window.location.search).get('variationId');
        if (variationId === null) {
            const fragment = o_util.fragment.deserialize<{ variationId: string }>(false);
            return fragment.variationId;
        }

        return variationId;
    }

    private updateURL(variationId: string): void {
        const fragment = o_util.fragment.deserialize(false);
        fragment.variationId = variationId;
        const serializedFragment = o_util.fragment.serialize(fragment) ?? '';

        const searchParams = new URLSearchParams(window.location.search);
        if (!!searchParams.get('variationId')) {
            searchParams.set('variationId', variationId);
        }
        const searchString = searchParams.toString() ? `?${searchParams.toString()}` : '';

        window.history.replaceState(null, '', `${window.location.pathname}${searchString}${serializedFragment}`);
    }

    private setEnteredArticleNumberAndPromotionToOProduct() {
        const articleNumberAndPromotion = o_util.fragment.get('artNr');
        if (articleNumberAndPromotion) {
            o_product.data.enteredArticleNumberAndPromotion = `${articleNumberAndPromotion}`; // Conversion to string is needed because variable could be a number at runtime - seems to be wrong in the typings - (see test that fails when you remove the string conversion)
            o_util.fragment.remove('artNr');
        }
    }

    /**
     * sets tracking eventMergeId, need this, only after variation change,
     * so that tracking will not track page impressions anymore, but event with mergeId
     * @param eventMergeId if given, sets the value,generates new one otherwise
     */
    protected setTrackingEventMergeIdToOProduct(eventMergeId?: string): void {
        setGlobalEventMergeId(eventMergeId);
    }

    private destroyObsleteComponentsReloadCallbacks(): void {
        window?.o_product.reloadCallbacks?.forEach(callback => {
            o_global.eventQBus.off(RELOAD_COMPONENT_TOPIC, callback);
        });
        if (window?.o_product?.reloadCallbacks) {
            o_product.reloadCallbacks = [];
        }
    }

    protected registerDimensionChangedEventHandler(): void {
        o_global.eventQBus.on(DIMENSION_SELECTED_TOPIC, async (data: DimensionsStateEvent) => {
            await this.lockExecution(async () => {
                const currentVariationId = this.getRenderedVariationId();
                const dimensionsState = data.dimensionStates;
                const selectedState = data.selectedState;
                this.destroyObsleteComponentsReloadCallbacks();
                await this.renderReloadableComponents(component => this.renderComponent(component, currentVariationId, dimensionsState));

                this.setVariationChangedGlobalState();

                const variationIdAfterRerendering = this.getRenderedVariationId();
                this.updateURL(variationIdAfterRerendering);

                this.setTrackingEventMergeIdToOProduct(generateEventMergeId());

                this.trackDimensionChange(
                    selectedState,
                    data.dimensionTypeUsed,
                    data.dimensionEnabled,
                    data.triggeredByDimensionSelectionSheet
                );

                this.writeVariationIdToCookie();

                this.emitComponentsHtmlLoadedEvent();
            });
        });
    }

    private registerDimensionChangedInDimensionSelectionSheetHandler(): void {
        o_global.eventQBus.on(DIMENSION_SELECTED_IN_SHEET_TOPIC, async (data: DimensionsStateEvent) => {
            await this.lockExecution(async () => {
                const currentVariationId = this.getRenderedVariationId();
                const dimensionsState = data.dimensionStates;
                const dimensionSelectionSheetComponent = document.querySelector<HTMLElement>('.js_pdp_dimension-selection-sheet-content');
                if (!dimensionSelectionSheetComponent) return;
                await this.renderDimensionSelectionSheetComponent(dimensionSelectionSheetComponent, currentVariationId, dimensionsState);
                this.trackSheetDimensionChange(data.selectedState, data.dimensionEnabled);
                o_global.eventQBus.emit(RELOAD_DIMENSION_SELECTION_SHEET_COMPONENT_TOPIC, {});
            });
        });
    }

    private async renderDimensionSelectionSheetComponent(
        component: HTMLElement,
        variationId: string,
        dimensionsState: DimensionValueByType[]
    ): Promise<void> {
        try {
            const offerId = this.getRootElement().getAttribute('data-offer-id');
            const productId = this.getRootElement().getAttribute('data-product-id');
            const offerOrProductId = (offerId || productId) as string;
            const params = new URLSearchParams({ variationId });
            const fetchUrl = `/dcs/dimension-selection-sheet-content/${offerOrProductId}?${params}`;
            const response = await this.fetchComponentHtml(fetchUrl, dimensionsState);
            if (!response.ok) return;
            const componentHtml = await response.text();
            this.setComponentHtmlContent(component, componentHtml);
        } catch (e) {
            this.logError({
                error: e as Error,
                data: {
                    currentVariation: variationId,
                    dimensionsState: JSON.stringify(dimensionsState),
                },
            });
            this.removeComponentContent(component);
        }
    }

    protected getRenderedVariationId(): string {
        const currentVariationId = document.querySelector<HTMLElement>(this.rootSelector)?.dataset.variationId;

        if (!currentVariationId) {
            throw new Error('can not find variation id');
        }

        return currentVariationId;
    }

    /**
     * emits event that lets all components js run their "onHtmlLoaded()" method
     */
    protected emitComponentsHtmlLoadedEvent(): void {
        o_global.eventQBus.emit(RELOAD_COMPONENT_TOPIC, {});
    }

    /**
     * @return all components that should react on variation change
     */
    protected getReloadableComponents(): Array<HTMLElement> {
        const nodeListOfElements = document.querySelectorAll<HTMLElement>('div[data-pdp-ui-aggregator-component]');
        return Array.from(nodeListOfElements);
    }

    /**
     * replaces `component`s html with `htmlString`
     */
    protected setComponentHtmlContent(component: HTMLElement, htmlString: string): void {
        if (!component.parentNode) {
            console.warn('Cannot update outerHTML of component because the component has no parent.');
            return;
        }
        component.outerHTML = htmlString;
    }

    protected removeComponentContent(component: HTMLElement): void {
        if (component.parentNode) {
            this.setComponentHtmlContent(component, '');
        } else {
            console.warn("couldn't remove html content from component because the component had no parent");
        }
    }

    protected async renderReloadableComponents(componentMap: (component: HTMLElement) => Promise<void>): Promise<void> {
        const reloadableComponents = this.getReloadableComponents();
        const renderPromises = reloadableComponents.map(componentMap);
        await Promise.all(renderPromises);
    }

    /**
     * query DCS and render component in place in HTML
     * @param component HTML component to re-render
     * @param currentVariation current loaded variation id
     * @param dimensionsState currently clicked dimensions
     */
    protected async renderComponent(
        component: HTMLElement,
        currentVariation: string,
        dimensionsState: DimensionValueByType[]
    ): Promise<void> {
        try {
            const offerId = this.getRootElement().getAttribute('data-offer-id');
            const productId = this.getRootElement().getAttribute('data-product-id');
            const offerOrProductId = (offerId || productId) as string;
            const fetchUrl = this.getComponentFetchUrl(component, currentVariation, offerOrProductId);
            let response = await this.fetchComponentHtml(fetchUrl, dimensionsState);

            if (response.status === 401 && this.isRedirectToggleActive) {
                this.logError({ message: createStatusCodeErrorMessage(response.status) });
                redirectToPreviousVariation(currentVariation);
                return;
            }

            if (response.status === 503 && this.isErrorPageToggleActive) {
                response = await this.fetchErrorPage();
            }

            const componentHtml = await response.text();
            this.setComponentHtmlContent(component, componentHtml);
        } catch (e) {
            this.logError({
                error: e as Error,
                data: {
                    currentVariation: currentVariation,
                    dimensionsState: JSON.stringify(dimensionsState),
                },
            });
            this.removeComponentContent(component);
        }
    }

    private async fetchErrorPage(): Promise<Response> {
        return await fetch(`${window.location.origin}/static-proxy/product-assets/error.html`);
    }

    private async renderComponentsByVariationId(variationId: string) {
        await this.renderReloadableComponents(component => this.renderComponentByVariationId(component, variationId));
    }

    protected async renderComponentByVariationId(component: HTMLElement, variationId: string) {
        try {
            const componentHtml = await this.fetchComponentByVariationId(component, variationId);
            this.setComponentHtmlContent(component, componentHtml);
        } catch (e) {
            this.logError({
                error: e as Error,
                data: {
                    currentVariation: variationId,
                },
            });
        }
    }

    protected async fetchComponentByVariationId(component: HTMLElement, variationId: string): Promise<string> {
        const offerId = this.getRootElement().getAttribute('data-offer-id');
        const productId = this.getRootElement().getAttribute('data-product-id');
        const offerOrProductId = (offerId || productId) as string;
        const fetchUrl = this.getComponentFetchUrl(component, variationId, offerOrProductId);

        const response = await fetch(fetchUrl, {
            method: 'GET',
            headers: {
                'initial-referrer': this.initialReferrer,
                'x-requested-with': 'XMLHttpRequest',
            },
        });

        if (response.status !== 200) {
            const referenceVariationId = o_util.cookie.get('ft5-pdp-reference-variation-id') as string;
            if (referenceVariationId) {
                window.location.hash = `#variationId=${referenceVariationId}`;
            }
            o_global.eventQBus.emitRetain(VARIATION_LOAD_FAILED_EVENT);
            throw new Error(
                `Could not fetch rendered component from DCS url ${fetchUrl}. Response (${response.status}): ${await response.text()}`
            );
        }

        return response.text();
    }

    /**
     * load component form DCS
     */
    protected async fetchComponentHtml(fetchUrl: string, dimensionsState: DimensionValueByType[]): Promise<Response> {
        const [sessionId, otp] = this.calculateOneTimePad();

        let fetchFn;
        if (window.AwsWafIntegration === undefined) {
            this.logger?.logError({ message: 'AwsWafIntegration not found! Reverting back to unprotected fetch' });
            fetchFn = fetch;
        } else {
            fetchFn = window.AwsWafIntegration.fetch;
        }

        return await fetchFn(fetchUrl, {
            method: 'POST',
            body: JSON.stringify({ dimensions: dimensionsState }),
            headers: {
                'initial-referrer': this.initialReferrer,
                'content-type': 'application/json',
                'ft5-pdp-otp': otp ?? '',
                'ft5-pdp-session': sessionId ?? '',
                'x-requested-with': 'XMLHttpRequest',
            },
        });
    }

    private calculateOneTimePad(): [string | undefined, string | undefined] {
        const operand1Element = document.querySelector('input[name="ft5-pdp-op1"]') as HTMLInputElement;
        const operand2Element = document.querySelector('input[name="ft5-pdp-op2"]') as HTMLInputElement;
        const sessionIdElement = document.querySelector('input[name="ft5-pdp-session"]') as HTMLInputElement;

        if (!operand1Element || !operand2Element || !sessionIdElement || typeof BigInt === 'undefined') {
            return [undefined, undefined];
        }

        const otpOperand1 = BigInt(`0x${operand1Element.value}`);
        const otpOperand2 = BigInt(`0x${operand2Element.value}`);
        const otp = otpOperand1 ^ otpOperand2;

        return [sessionIdElement.value, otp.toString(16)];
    }

    /**
     * @returns built backend url for component fetch
     */
    protected getComponentFetchUrl(component: HTMLElement, variationId: string, offerOrProductId: string): string {
        const componentName = component.dataset.pdpUiAggregatorComponent;
        const params = new URLSearchParams({ variationId });

        if (!componentName) {
            throw new Error(`cant read component name from html data: "${component.outerHTML}"`);
        }
        return `/dcs/${componentName}/${offerOrProductId}?${params}`;
    }

    /**
     * wait until page script tags are loaded (readyState !== 'loading')
     */
    private async waitPageJsIsLoaded(): Promise<void> {
        await waitForCondition(() => document.readyState !== 'loading');
    }

    /**
     * sets global state for isVariationChanged to true
     */
    private setVariationChangedGlobalState(value = true): void {
        window.o_product.state.isVariationChanged = value;
    }

    private trackSheetDimensionChange(selectedState: DimensionValueByType[], dimensionEnabled: boolean | undefined): void {
        this.trackEvent({
            product_DimensionSheet: selectedState.map(state => state.type).join('_'),
            ...(dimensionEnabled !== undefined ? { product_DimensionEnabled: String(dimensionEnabled) } : {}),
        });
    }

    private trackDimensionChange(
        selectedState: DimensionValueByType[],
        dimensionTypeUsed: ColorUiType | undefined,
        dimensionEnabled: boolean | undefined,
        triggeredByDimensionSelectionSheet: boolean | undefined
    ): void {
        this.trackView({
            product_Dimension: selectedState.map(state => state.type).join('_'),
            ...(dimensionTypeUsed ? { product_DimensionTypeUsed: dimensionTypeUsed } : {}),
            ...(dimensionEnabled !== undefined ? { product_DimensionEnabled: String(dimensionEnabled) } : {}),
            ...(triggeredByDimensionSelectionSheet ? { product_Dimension: 'color_cover' } : {}),
        });
    }
}
